import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { getLocalTimeZone, now, toCalendarDateTime, today, toZoned } from '@internationalized/date';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { IoMdTime } from 'react-icons/io';
import {
  Accordion,
  AccordionItem,
  Button,
  DateValue,
  ScrollShadow,
  Spinner,
  TmdxDatepicker,
  TmdxSelect,
  Tooltip,
} from '@ds';
import { AirportListItemType } from '@lp/features/RoutePlanner/types';
import {
  BaseCommercialAviaLegSchemaValues,
  ConnectedLegSchemaType,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  RouteLegFormMode,
  useLegFormContext,
} from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { useAirports } from '@lp/features/RoutePlanner/hooks/useAirports';
import { removeTimeZoneName, renderSelectItem } from '@/utils/helpers';
import { routePlannerQueries } from '@/queries/route-planner';
import { LegLocationTypeEnum, RecommendedFlightType } from '@/api/apis/route';
import { convertSabreFlightLegToCommercialAviaFormValues, getCommercialZonedDateTime } from './utils';
import { RecommendedFlight } from './RecommendedFlight';
import { useWatchFields } from '@/hooks/useWatchFields';

enum SearchByEnum {
  Departure = 'Departure',
  Arrival = 'Arrival',
}

const SABRE_AIRPORTS_QUERY = { sabreSupportedOnly: true, withoutFbo: true };

const SEARCH_BY_SELECT_LIST = [
  {
    value: SearchByEnum.Departure,
    name: SearchByEnum.Departure,
  },
  {
    value: SearchByEnum.Arrival,
    name: SearchByEnum.Arrival,
  },
];

export const SearchRecommendedFlights = () => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null);
  const [departureAirportId, setDepartureAirportId] = useState<string | null>(null);
  const [arrivalAirportId, setArrivalAirportId] = useState<string | null>(null);
  const [searchByType, setSearchByType] = useState<SearchByEnum>(SearchByEnum.Departure);
  const [searchDateTimeStart, setSearchDateTimeStart] = useState<DateValue | null>(null);
  const [searchDateTimeEnd, setSearchDateTimeEnd] = useState<DateValue | null>(null);
  const [recommendedFlightsList, setRecommendedFlightsList] = useState<RecommendedFlightType[] | null>(null);
  const [recommendedStartAirport, setRecommendedStartAirport] = useState<AirportListItemType | null>(null);
  const [recommendedEndAirport, setRecommendedEndAirport] = useState<AirportListItemType | null>(null);
  const { mode } = useLegFormContext();
  const { reset, getValues } = useFormContext<BaseCommercialAviaLegSchemaValues>();
  const { resources } = useWatchFields<BaseCommercialAviaLegSchemaValues>(['resources'] as const);

  const isDisable = mode === RouteLegFormMode.Edit;

  useEffect(() => {
    const nowDate = toCalendarDateTime(now(getLocalTimeZone()));

    setSearchDateTimeStart(nowDate.add({ hours: 2 }));
    setSearchDateTimeEnd(nowDate.add({ hours: 4 }));
  }, []);

  const {
    airportsList: departureAirportsList,
    onAirportSearch: onDepartureAirportSearch,
    setAirportsAutocompleteOpen: setDepartureAirportAutocompleteOpen,
    selectedAirport: selectedDepartureAirport,
    searchValue: searchDepartureAirportValue,
    airportsScrollerRef: departureAirportScrollerRef,
    isAirportsListLoading: isDepartureAirportListLoading,
    onAirportSelectChange: onDepartureAirportSelectChange,
  } = useAirports({
    setValue: (v: string) => setDepartureAirportId(v),
    filters: SABRE_AIRPORTS_QUERY,
  });

  const {
    airportsList: arrivalAirportsList,
    onAirportSearch: onArrivalAirportSearch,
    setAirportsAutocompleteOpen: setArrivalAirportAutocompleteOpen,
    selectedAirport: selectedArrivalAirport,
    searchValue: searchArrivalAirportValue,
    airportsScrollerRef: arrivalAirportScrollerRef,
    isAirportsListLoading: isArrivalAirportListLoading,
    onAirportSelectChange: onArrivalAirportSelectChange,
  } = useAirports({
    setValue: (v: string) => setArrivalAirportId(v),
    filters: SABRE_AIRPORTS_QUERY,
  });

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setRecommendedFlightsList(null);

      if (departureAirportId && arrivalAirportId && searchDateTimeStart && searchDateTimeEnd) {
        const timezone =
          searchByType === SearchByEnum.Departure
            ? selectedDepartureAirport?.location.timezone
            : selectedArrivalAirport?.location.timezone;

        const data = await queryClient.fetchQuery(
          routePlannerQueries.getRecommendedFlights({
            startAirportId: departureAirportId,
            endAirportId: arrivalAirportId,
            fromDate: removeTimeZoneName(toZoned(searchDateTimeStart, timezone || getLocalTimeZone()).toString()),
            tillDate: removeTimeZoneName(toZoned(searchDateTimeEnd, timezone || getLocalTimeZone()).toString()),
            passengerCount: resources.length,
            searchType: searchByType,
          }),
        );

        setRecommendedFlightsList(data.flights);
      }
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const searchValidation = useMemo<{ isValid: boolean; messages: string[] }>(() => {
    const nowDateTime = now(getLocalTimeZone());
    const nowDate = today(getLocalTimeZone());
    let messages: string[] = [];

    if (!resources.length) {
      messages = [...messages, 'Select at least one resource'];
    }

    if (!departureAirportId) {
      messages = [...messages, 'Select Departure Airport'];
    }

    if (!arrivalAirportId) {
      messages = [...messages, 'Select Arrival Airport'];
    }

    if (!searchDateTimeStart || !searchDateTimeEnd) {
      messages = [...messages, 'Provide Date'];
    } else {
      const isPastDate = searchDateTimeStart.compare(nowDate) < 0 || searchDateTimeEnd.compare(nowDate) < 0;

      if (isPastDate) {
        messages = [...messages, 'Provided date can not be in the past'];
      }
    }

    if (searchDateTimeStart) {
      const rangeStart = searchDateTimeStart;
      const timezone =
        searchByType === SearchByEnum.Departure
          ? selectedDepartureAirport?.location.timezone
          : selectedArrivalAirport?.location.timezone;

      const isPastTime = nowDateTime.compare(toZoned(rangeStart, timezone || getLocalTimeZone())) > 0;

      if (isPastTime) {
        messages = [...messages, 'Provided date and time can not be in the past'];
      }
    }

    if (searchDateTimeStart && searchDateTimeEnd) {
      const isCorrectRange = searchDateTimeEnd.compare(searchDateTimeStart) > 0;

      if (!isCorrectRange) {
        messages = [...messages, 'Date range end must be after the date range start'];
      }
    }

    return {
      isValid: !messages.length,
      messages,
    };
  }, [
    resources,
    departureAirportId,
    arrivalAirportId,
    searchDateTimeStart,
    searchDateTimeEnd,
    searchByType,
    selectedDepartureAirport?.location.timezone,
    selectedArrivalAirport?.location.timezone,
  ]);

  const handleFlightSelect = (flight: RecommendedFlightType) => {
    const flightLegs = flight.legs.map<ConnectedLegSchemaType>((l) => {
      return convertSabreFlightLegToCommercialAviaFormValues(flight, l);
    });

    const departureTimeZoned = getCommercialZonedDateTime(flight.departureTimeLocal);
    const arrivalTimeZoned = getCommercialZonedDateTime(flight.departureTimeLocal);

    const newValues = {
      departureLocation: {
        id: flight.departureAirportId,
        locationType: LegLocationTypeEnum.airport,
        locationTypeId: LegLocationTypeEnum.airport,
        displayName: flight.departureAirportIATA,
        fboId: '',
        fboAddressId: 0,
        maoId: '',
        timezone: departureTimeZoned.timeZone,
        longitude: null,
        latitude: null,
      },
      arrivalLocation: {
        id: flight.arrivalAirportId,
        locationType: LegLocationTypeEnum.airport,
        locationTypeId: `${LegLocationTypeEnum.airport}|${flight.arrivalAirportId}`,
        displayName: flight.departureAirportIATA,
        fboId: '',
        fboAddressId: 0,
        maoId: '',
        timezone: arrivalTimeZoned.timeZone,
        longitude: null,
        latitude: null,
      },
      departureDateTimeValue: departureTimeZoned,
      departureLocalTimezone: departureTimeZoned.timeZone,
      departureDateTime: departureTimeZoned.toString(),
      arrivalDateTimeValue: arrivalTimeZoned,
      arrivalLocalTimezone: arrivalTimeZoned.timeZone,
      arrivalDateTime: arrivalTimeZoned.toString(),
      priceInCents: `${Math.round(flight.price)}`,
      flightNumber: flight.legs[0].flightNumber,
      bookingNumber: '',
      connectedLegs: flightLegs,
      commercialFlight: flight,
    };

    reset(
      {
        ...getValues(),
        ...newValues,
      } as BaseCommercialAviaLegSchemaValues,
      {
        keepDefaultValues: true,
        keepDirty: true,
        keepTouched: true,
      },
    );

    setSelectedFlightId(flight.id);
  };

  const handleDepartureAirportSelect = async (id: string) => {
    const airport = await onDepartureAirportSelectChange(id);

    setRecommendedStartAirport(airport);
  };

  const handleArrivalAirportSelect = async (id: string) => {
    const airport = await onArrivalAirportSelectChange(id);

    setRecommendedEndAirport(airport);
  };

  return (
    <Accordion
      isDisabled={isDisable}
      variant="splitted"
      className="p-0"
      itemClasses={{
        base: 'bg-white shadow-none rounded-lg',
        title: 'text-m-m! text-base-5',
        indicator: 'text-base-20 data-[open=true]:rotate-180',
        content: 'flex flex-col gap-4 items-stretch pb-4',
      }}
    >
      <AccordionItem
        as="div"
        key="searchCommercialFlights"
        aria-label="Search for flights"
        title="Search for flights"
        indicator={<MdOutlineKeyboardArrowDown size={20} />}
      >
        <div
          className="grid gap-4 w-full"
          style={{
            gridTemplateColumns: `repeat(3, minmax(0, 1fr))`,
          }}
        >
          <TmdxSelect
            label="Departure Airport *"
            placeholder="Choose an option"
            aria-label="Departure Airport *"
            scrollRef={departureAirportScrollerRef}
            items={departureAirportsList}
            isLoading={isDepartureAirportListLoading}
            disallowEmptySelection
            onOpenChange={setDepartureAirportAutocompleteOpen}
            selectedKeys={departureAirportId ? [departureAirportId] : []}
            disabledKeys={departureAirportId ? [departureAirportId] : []}
            onSelectionChange={(key) => handleDepartureAirportSelect(key.currentKey as string)}
            onSearchValue={onDepartureAirportSearch}
            searchValue={searchDepartureAirportValue}
          >
            {renderSelectItem}
          </TmdxSelect>
          <TmdxSelect
            label="Arrival Airport *"
            placeholder="Choose an option"
            aria-label="Arrival Airport *"
            scrollRef={arrivalAirportScrollerRef}
            items={arrivalAirportsList}
            isLoading={isArrivalAirportListLoading}
            disallowEmptySelection
            onOpenChange={setArrivalAirportAutocompleteOpen}
            selectedKeys={arrivalAirportId ? [arrivalAirportId] : []}
            disabledKeys={arrivalAirportId ? [arrivalAirportId] : []}
            onSelectionChange={(key) => handleArrivalAirportSelect(key.currentKey as string)}
            onSearchValue={onArrivalAirportSearch}
            searchValue={searchArrivalAirportValue}
          >
            {renderSelectItem}
          </TmdxSelect>
          <TmdxSelect
            aria-label="Search Date/time *"
            label="Search Date/time *"
            disallowEmptySelection
            selectedKeys={searchByType ? [searchByType] : []}
            items={SEARCH_BY_SELECT_LIST}
            onChange={(e) => setSearchByType(e.target.value as SearchByEnum)}
          >
            {renderSelectItem}
          </TmdxSelect>
        </div>
        <div
          className="grid gap-4 w-full"
          style={{
            gridTemplateColumns: `repeat(2, minmax(0, 1fr))`,
          }}
        >
          <TmdxDatepicker
            size="md"
            aria-label="Date Start"
            label="Date/Time Start *"
            hideTimeZone
            hourCycle={24}
            showMonthAndYearPickers={false}
            granularity="minute"
            value={searchDateTimeStart}
            onChange={setSearchDateTimeStart}
            endContent={
              <div className="pointer-events-none flex items-center">
                <span className="text-default-400 text-small">
                  <IoMdTime size={16} />
                </span>
              </div>
            }
          />
          <TmdxDatepicker
            size="md"
            aria-label="Date End"
            label="Date/Time End *"
            hideTimeZone
            hourCycle={24}
            showMonthAndYearPickers={false}
            granularity="minute"
            value={searchDateTimeEnd}
            onChange={setSearchDateTimeEnd}
            endContent={
              <div className="pointer-events-none flex items-center">
                <span className="text-default-400 text-small">
                  <IoMdTime size={16} />
                </span>
              </div>
            }
          />
        </div>
        <div className="flex justify-end">
          <Tooltip
            isDisabled={searchValidation.isValid}
            content={
              <div className="flex flex-col gap-0.5">
                {searchValidation.messages.map((m) => (
                  <span key={m} className="text-s-r text-black">
                    {m}
                  </span>
                ))}
              </div>
            }
          >
            <div>
              <Button
                size="sm"
                variant="outline"
                isDisabled={!searchValidation.isValid}
                isLoading={isLoading}
                onPress={handleSearch}
              >
                Search
              </Button>
            </div>
          </Tooltip>
        </div>
        {isLoading && !recommendedFlightsList && (
          <div className="w-full flex justify-center items-center py-4">
            <Spinner size="sm" />
          </div>
        )}
        {recommendedFlightsList && (
          <div className="w-full">
            {!recommendedFlightsList.length ? (
              <div className="w-full flex justify-center items-center py-4">
                <span className="text-s-sb text-base-20">No recommended flights were found</span>
              </div>
            ) : (
              <ScrollShadow visibility="none" className="w-full flex flex-col gap-2 max-h-44">
                {recommendedFlightsList.map((flight) => (
                  <RecommendedFlight
                    key={flight.id}
                    selectedFlightId={selectedFlightId}
                    flight={flight}
                    departureAirport={recommendedStartAirport}
                    arrivalAirport={recommendedEndAirport}
                    onFlightSelect={handleFlightSelect}
                  />
                ))}
              </ScrollShadow>
            )}
          </div>
        )}
      </AccordionItem>
    </Accordion>
  );
};
